import { useState, type ReactElement } from 'react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessOutlined from '@mui/icons-material/SettingsBrightnessOutlined';

type Mode = 'light' | 'dark' | 'system';
const MODES: { mode: Mode; label: string; icon: ReactElement }[] = [
  { mode: 'light', label: 'Light', icon: <LightModeOutlined fontSize="small" /> },
  { mode: 'dark', label: 'Dark', icon: <DarkModeOutlined fontSize="small" /> },
  { mode: 'system', label: 'Device', icon: <SettingsBrightnessOutlined fontSize="small" /> },
];

/**
 * Light, dark, or follow the device. In Preferences (`showLabels`) it is three labelled buttons;
 * in the top bar it is one icon that opens a short list.
 */
export function ColorModeToggle({ showLabels }: { showLabels?: boolean }) {
  const { mode, setMode } = useColorScheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = mode ?? 'system';
  const here = MODES.find((m) => m.mode === current)!;

  if (showLabels) {
    return (
      <ToggleButtonGroup
        exclusive
        color="primary"
        value={current}
        onChange={(_e, v: Mode | null) => v && setMode(v)}
        aria-label="Appearance"
      >
        {MODES.map((m) => (
          <ToggleButton key={m.mode} value={m.mode} sx={{ textTransform: 'none', gap: 0.75, px: 2 }}>
            {m.icon}
            {m.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }
  return (
    <>
      <Tooltip title="Appearance">
        <IconButton aria-label="Appearance" aria-haspopup="menu" onClick={(e) => setAnchor(e.currentTarget)}>
          {here.icon}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {MODES.map((m) => (
          <MenuItem
            key={m.mode}
            selected={m.mode === current}
            onClick={() => {
              setMode(m.mode);
              setAnchor(null);
            }}
          >
            <ListItemIcon>{m.icon}</ListItemIcon>
            <ListItemText>{m.label}</ListItemText>
            {m.mode === current && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
